import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import ErrorOutlineIcon from 'components/material-symbol/icons/ErrorOutline'
import RouterLinkButton from 'components/router-link-button'
import useStyles from './styles'

const StatusHero = ({ icon, error, title, subtitle, children, ...rest }) => {
  const classes = useStyles()

  return (
    <div className={classes.page} {...rest}>
      <div className={classes.column}>
        <div className={classes.hero}>
          <div
            className={clsx(
              classes.iconCircle,
              error && classes.iconCircleError,
            )}
            aria-hidden="true"
          >
            {icon}
          </div>
          <Typography variant="h4" component="h1" className={classes.title}>
            {title}
          </Typography>
          <p className={classes.subtitle}>{subtitle}</p>
        </div>
        {children && <div className={classes.actions}>{children}</div>}
      </div>
    </div>
  )
}

StatusHero.propTypes = {
  icon: PropTypes.node.isRequired,
  error: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  children: PropTypes.node,
}

// The payment is still `pending` / `processing`, or it settled and the booking
// is not loadable yet. Same hero as the confirmation so the page does not jump.
export const PurchasePending = () => (
  <StatusHero
    icon={<CircularProgress size={28} color="inherit" />}
    title="Confirming Your Payment"
    subtitle="This usually takes a few seconds. Please keep this page open."
    role="status"
    data-testid="private-lesson-pending"
  />
)

// The payment was canceled (the slot may be gone). `retryTo` goes back to the
// coach's schedule so the golfer can pick a time again.
export const PurchaseFailed = ({ retryTo }) => {
  const classes = useStyles()

  return (
    <StatusHero
      error
      icon={<ErrorOutlineIcon />}
      title="Purchase Not Completed"
      subtitle="Unable to complete your purchase. The selected time slot may no longer be available. Please try again or select a different time."
      data-testid="private-lesson-failed"
    >
      {retryTo && (
        <RouterLinkButton
          variant="contained"
          color="primary"
          className={classes.button}
          to={retryTo}
        >
          Retry
        </RouterLinkButton>
      )}
    </StatusHero>
  )
}

PurchaseFailed.propTypes = { retryTo: PropTypes.string }

// Stripe could not be asked about the payment, or the booking could not be
// loaded after the payment settled. Both are "reload and check your email"
// states rather than a failed purchase.
export const PurchaseFallback = ({ verified, onReload }) => {
  const classes = useStyles()

  return (
    <StatusHero
      error={!verified}
      icon={<ErrorOutlineIcon />}
      title={verified ? 'Payment Received' : "We Couldn't Verify Your Payment"}
      subtitle={
        verified
          ? 'There was an issue loading your booking details. Please try reloading the page. You will receive a confirmation email shortly.'
          : 'Payment verification failed. Please try reloading the page.'
      }
      data-testid="private-lesson-fallback"
    >
      <Button
        variant="contained"
        color="primary"
        className={classes.button}
        onClick={onReload}
      >
        Reload Page
      </Button>
    </StatusHero>
  )
}

PurchaseFallback.propTypes = {
  verified: PropTypes.bool,
  onReload: PropTypes.func.isRequired,
}
